import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { FunnelChart, Funnel, ResponsiveContainer, LabelList } from 'recharts';

import messages from './messages';
import Colors from '../utilities/colors';
import { emptyObject } from 'ui-components/default-props/empty';
import { doubleDash } from '../../../constants';

const LABEL_OFFSET = 30;

const emailKey = {
  PROCESSED: 'processed',
  DELIVERED: 'delivered',
  OPEN: 'open',
  CLICK: 'click',
};

const dataProcessingOrder = [emailKey.CLICK, emailKey.OPEN, emailKey.DELIVERED, emailKey.PROCESSED];

const formatValue = (value, total) => {
  if (!value) {
    return 0;
  }
  return value / total;
};

const formatData = (data) => {
  const cache = {
    [emailKey.CLICK]: 0,
    [emailKey.OPEN]: 0,
    [emailKey.DELIVERED]: 0,
    [emailKey.PROCESSED]: 0,
  };
  const totalCount = dataProcessingOrder.reduce((acc, key) => {
    const sum = acc + data[key];
    cache[key] = sum;
    return sum;
  }, 0);
  return dataProcessingOrder
    .map((key) => {
      return {
        key,
        value: formatValue(cache[key], totalCount),
        originalValue: cache[key],
      };
    }, [])
    .reverse();
};

const NotificationEmailsGraph = ({ data = emptyObject }) => {
  const intl = useIntl();

  const dataIsNull = useMemo(() => data?.processed == null && data?.delivered == null && data?.open == null && data?.click == null, [data]);

  const renderCustomizedCenterLabel = useCallback(
    (labelProps) => {
      const { x, y, value, width: labelWidth, height: labelHeight } = labelProps;
      if (labelWidth < LABEL_OFFSET) {
        const offset = value > 0 ? LABEL_OFFSET : 0;
        return (
          <text dominantBaseline='central' fill='#000' textAnchor='middle' x={x + labelWidth / 2 + offset} y={y + labelHeight / 2}>
            {intl.formatNumber(value, { style: 'percent' })}
          </text>
        );
      }
      return (
        <text dominantBaseline='central' fill='#fff' textAnchor='middle' x={x + labelWidth / 2} y={y + labelHeight / 2}>
          {intl.formatNumber(value, { style: 'percent' })}
        </text>
      );
    },
    [intl],
  );

  const renderCustomizedLeftLabel = useCallback(
    (labelProps) => {
      const { y, value, height: labelHeight } = labelProps;
      const messageDescriptor = messages[value];
      return (
        <text dominantBaseline='central' textAnchor='start' x={0} y={y + labelHeight / 2}>
          {intl.formatMessage(messageDescriptor)}
        </text>
      );
    },
    [intl],
  );

  const renderCustomizedRightLabel = useCallback(
    (labelProps) => {
      const { y, value, parentViewBox, height: labelHeight } = labelProps;

      return (
        <text dominantBaseline='central' textAnchor='end' x={parentViewBox.width} y={y + labelHeight / 2}>
          {dataIsNull ? doubleDash : intl.formatNumber(value)}
        </text>
      );
    },
    [intl, dataIsNull],
  );

  const formattedData = formatData(data);
  return (
    <ResponsiveContainer height={220} width='100%'>
      <FunnelChart
        data={formattedData}
        layout='vertical'
        margin={{
          top: 20,
          right: 40,
          bottom: 0,
          left: 0,
        }}
      >
        <Funnel data={formattedData} dataKey='value' fill={Colors.BAR_COLOR_PURPLE} isAnimationActive>
          <LabelList content={renderCustomizedLeftLabel} dataKey='key' fill='#000' position='left' stroke='none' />
          {!dataIsNull && <LabelList content={renderCustomizedCenterLabel} dataKey='value' position='center' />}
          <LabelList content={renderCustomizedRightLabel} dataKey='originalValue' fill='#000' position='right' stroke='none' />
        </Funnel>
      </FunnelChart>
    </ResponsiveContainer>
  );
};

NotificationEmailsGraph.propTypes = {
  data: PropTypes.shape({
    processed: PropTypes.number,
    delivered: PropTypes.number,
    open: PropTypes.number,
    click: PropTypes.number,
  }),
};

export default NotificationEmailsGraph;
